import { useQueryClient } from "@tanstack/react-query";

/**
 * Custom hook for refreshing knowledge graph visualization
 * Invalidates the graph query and optionally calls an update callback
 *
 * @param graphId - The ID of the graph to refresh (optional, will skip refresh if not provided)
 * @param onGraphUpdate - Optional callback to execute after invalidating the query
 * @returns A function to refresh the graph
 *
 * @example
 * ```tsx
 * const refreshGraph = useRefreshGraph(graphId, onGraphUpdate);
 *
 * // Later, call it to refresh:
 * refreshGraph();
 * ```
 */
export const useRefreshGraph = (graphId?: string | null, onGraphUpdate?: () => void): (() => void) => {
	const queryClient = useQueryClient();

	return () => {
		if (!graphId) return;

		queryClient.invalidateQueries({
			queryKey: ["knowledgeGraphVisualization", graphId],
		});

		onGraphUpdate?.();
	};
};
